<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  progressSummary: {
    type: Object,
    default: () => ({}),
  },
  showAnalytics: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'analytics']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();
const numberFormatter = new Intl.NumberFormat();
const dashValue = '—';

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});

const showProgressRow = computed(() => !props.isLiveChatType);

const formatCount = value => {
  if (value === null || value === undefined) {
    return dashValue;
  }
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return dashValue;
  }
  return numberFormatter.format(numberValue);
};

const extractSummaryValue = (summary, keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (summary[key] !== undefined) {
      return summary[key];
    }
  }
  return undefined;
};

const progressCounts = computed(() => {
  if (!showProgressRow.value) {
    return null;
  }

  const summary = props.progressSummary || {};
  const hasSummary = Object.keys(summary).length > 0;

  if (!hasSummary) {
    return {
      estimatedTotal: dashValue,
      delivered: dashValue,
      // [PROSPEK] 2026-06-22 show queued as the tenant-facing non-final progress metric.
      queued: dashValue,
      // [/PROSPEK]
      failed: dashValue,
    };
  }

  return {
    estimatedTotal: formatCount(
      extractSummaryValue(summary, [
        'estimated_total',
        'estimatedTotal',
        'estimated',
      ])
    ),
    delivered: formatCount(summary.delivered),
    // [PROSPEK] 2026-06-22 keep pending as API fallback while rendering queued semantics.
    queued: formatCount(extractSummaryValue(summary, ['queued', 'pending'])),
    // [/PROSPEK]
    failed: formatCount(summary.failed),
  };
});

const progressRowText = computed(() => {
  if (!showProgressRow.value) {
    return '';
  }

  const counts = progressCounts.value || {
    estimatedTotal: dashValue,
    delivered: dashValue,
    // [PROSPEK] 2026-06-22 show queued as the tenant-facing non-final progress metric.
    queued: dashValue,
    // [/PROSPEK]
    failed: dashValue,
  };

  return t('CAMPAIGN.PROGRESS.ROW_TEXT', counts);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
      <p
        v-if="showProgressRow"
        class="text-xs text-n-slate-11 w-full line-clamp-2"
      >
        {{ progressRowText }}
      </p>
    </div>
    <div class="flex items-center justify-end w-20 gap-2">
      <Button
        v-if="showAnalytics"
        v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-no-axes-column"
        :title="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        @click="emit('analytics')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
